import {GitBranchIcon, TagIcon, TriangleDownIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, type AnchoredOverlayProps, useFocusZone} from '@primer/react'
import {useCallback, useId, useMemo, useRef} from 'react'

import type {RefSelectorProps, RefType} from './RefSelector'
import {FocusKeys} from '@primer/behaviors'

import styles from './RefSelectorAnchoredOverlay.module.css'
import {clsx} from 'clsx'
import {GlobalCommands} from '@github-ui/ui-commands'

type PickedRefSelectorProps = Pick<
  RefSelectorProps,
  'hotKey' | 'uiCommandId' | 'onOpenChange' | 'size' | 'variant' | 'anchoredOverlayProps'
>

export interface RefSelectorAnchoredOverlayProps extends PickedRefSelectorProps {
  ariaDescribedBy?: string
  ariaLabelledBy?: string
  ariaLabel?: string
  overlayOpen: boolean
  onOverlayChange: (open: boolean) => void
  preventClosing?: boolean
  focusTrapEnabled?: boolean
  refType: RefType
  inputRef?: React.RefObject<HTMLInputElement | null>
  buttonClassName?: string
  buttonText?: string
  persistButtonText?: boolean
  buttonPrefix?: string
  showIcon?: boolean
  allowResizing?: boolean
  idEnding?: string
  useFocusZone?: boolean
  controlledPanelsEnabled: boolean
  displayCommitish: string
  disabled?: boolean
  renderAnchor?: AnchoredOverlayProps['renderAnchor']
  anchorRef?: React.RefObject<HTMLButtonElement | null>
}

export function RefSelectorAnchoredOverlay(props: React.PropsWithChildren<RefSelectorAnchoredOverlayProps>) {
  const {
    ariaDescribedBy,
    ariaLabelledBy,
    ariaLabel,
    hotKey,
    uiCommandId,
    onOpenChange,
    size,
    displayCommitish,
    variant,
    refType,
    children,
    preventClosing,
    inputRef,
    overlayOpen,
    onOverlayChange,
    focusTrapEnabled = true,
    buttonClassName,
    buttonText,
    persistButtonText = true,
    buttonPrefix,
    showIcon = true,
    allowResizing,
    useFocusZone: focusZoneEnabled,
    controlledPanelsEnabled,
    disabled,
    anchoredOverlayProps,
    renderAnchor,
    anchorRef,
  } = props

  const focusZoneContainerRef = useRef<HTMLDivElement>(null)
  useFocusZone(
    {
      containerRef: focusZoneContainerRef,
      disabled: !controlledPanelsEnabled || !focusZoneEnabled || !overlayOpen,
      bindKeys: FocusKeys.ArrowAll | FocusKeys.Tab,
      focusInStrategy: () => {
        const activeElement = document.activeElement
        return activeElement instanceof HTMLElement && focusZoneContainerRef.current?.contains(activeElement)
          ? activeElement
          : (inputRef?.current ?? undefined)
      },
      focusableElementFilter: element => element.getAttribute('role') !== 'tabpanel',
    },
    [controlledPanelsEnabled, refType],
  )

  // If we render multiple ref pickers on the page we need to ensure unique ids.
  const baseExtraIdEnding = useId()

  const extraIdEnding = `-${props.idEnding || baseExtraIdEnding}`
  const buttonId = `ref-picker${extraIdEnding}`

  const onOverlayOpenChange = useCallback(
    (open: boolean) => {
      onOverlayChange(open)
      onOpenChange?.(open)
    },
    [onOpenChange, onOverlayChange],
  )

  const focusTrapSettings = useMemo(() => {
    if (focusTrapEnabled) {
      // Setting disabled to false does not work. Therefore, I need to return different object
      return {initialFocusRef: inputRef}
    }

    return {initialFocusRef: inputRef, disabled: true}
  }, [focusTrapEnabled, inputRef])

  const resolveButtonTextString = () => {
    // If buttonPrefix is provided, we will always prepend it to the button text.
    // If buttonText is provided and persistButtonText is not set to false (it is true by default), use buttonText.
    // If buttonText is provided, and persistButtonText is false, use non-empty displayCommitish. If it is empty, use buttonText.
    // If buttonText is not provided, use displayCommitish as is.
    const nonEmptyDisplayCommitish = displayCommitish.length > 0
    return buttonText && persistButtonText ? buttonText : nonEmptyDisplayCommitish ? displayCommitish : buttonText || ''
  }

  const getButtonText = () => {
    const text = resolveButtonTextString()

    return buttonPrefix ? (
      <>
        <span className="fgColor-muted">{buttonPrefix}:</span>
        <span> {text}</span>
      </>
    ) : (
      text
    )
  }

  const getFallbackAriaLabel = () => {
    // Only append refType if displayCommitish doesn't already indicate the type
    // e.g., "all branches" or "all tags" already imply the type, so don't append
    const isDescriptive = displayCommitish === 'All branches' || displayCommitish === 'All tags'
    return isDescriptive ? displayCommitish : `${displayCommitish} ${refType}`
  }

  const getButtonAccessibleText = () => {
    if (!buttonPrefix) return undefined
    return `${buttonPrefix}: ${resolveButtonTextString()}`
  }

  return (
    <>
      <AnchoredOverlay
        open={overlayOpen}
        anchorRef={anchorRef}
        overlayProps={{
          role: 'dialog',
          width: 'medium',
          'aria-label': refType === 'branch' ? 'Select a branch' : 'Select a tag',
          initialFocusRef: inputRef,
        }}
        onOpen={() => onOverlayOpenChange(true)}
        // Prevent closing overlay, while working with the dialog
        onClose={() => !preventClosing && onOverlayOpenChange(false)}
        renderAnchor={anchorProps => {
          if (renderAnchor) {
            return renderAnchor({
              ...anchorProps,
              'aria-labelledby': ariaLabelledBy,
              disabled,
            })
          }
          return (
            <>
              <Button
                {...anchorProps}
                data-hotkey={hotKey}
                disabled={disabled}
                size={size}
                variant={variant}
                style={{
                  minWidth: allowResizing ? 0 : undefined,
                }}
                leadingVisual={showIcon ? (refType === 'tag' ? TagIcon : GitBranchIcon) : undefined}
                trailingVisual={TriangleDownIcon}
                aria-describedby={ariaDescribedBy}
                aria-labelledby={ariaLabelledBy}
                aria-label={
                  ariaLabelledBy ? undefined : (getButtonAccessibleText() ?? ariaLabel ?? getFallbackAriaLabel())
                }
                data-testid="anchor-button"
                id={buttonId}
                className={clsx(buttonClassName, styles.RefSelectorOverlayBtn)}
                data-icv-name="Switch branches/tags"
              >
                <div className={styles.RefSelectorOverlayContainer}>
                  <div
                    style={{
                      maxWidth: allowResizing ? undefined : 125,
                    }}
                    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                    className={clsx('ref-selector-button-text-container', styles.RefSelectorBtnTextContainer)}
                  >
                    <span className={styles.RefSelectorText}>{getButtonText()}</span>
                  </div>
                </div>
              </Button>
            </>
          )
        }}
        focusTrapSettings={focusTrapSettings}
        focusZoneSettings={
          controlledPanelsEnabled
            ? {disabled: true}
            : focusZoneEnabled
              ? {
                  bindKeys: FocusKeys.ArrowAll | FocusKeys.Tab,
                  focusInStrategy: () => inputRef?.current ?? undefined,
                }
              : {disabled: true}
        }
        {...anchoredOverlayProps}
      >
        {/* eslint-disable-next-line github/a11y-role-supports-aria-props */}
        <div
          ref={focusZoneContainerRef}
          data-testid="overlay-content"
          aria-labelledby={buttonId}
          id="selectPanel"
          style={{height: '100%'}}
        >
          {children}
        </div>
      </AnchoredOverlay>
      {uiCommandId && !disabled ? <GlobalCommands commands={{[uiCommandId]: () => onOverlayOpenChange(true)}} /> : null}
    </>
  )
}
